export type MariContactElapsed = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

const MS_PER_SECOND = 1_000;
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 3_600_000;
const MS_PER_DAY = 86_400_000;
const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const LOCAL_DATE_TIME_PATTERN = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

/** Elapsed since an ISO / timestamptz contact instant. */
export function computeElapsedSinceContactDate(
  contactedAt: string | null | undefined,
  nowMs: number = Date.now(),
): MariContactElapsed | null {
  const startMs = parseContactedAtMs(contactedAt);
  if (startMs == null) {
    return null;
  }

  const diffMs = Math.max(0, nowMs - startMs);
  const days = Math.floor(diffMs / MS_PER_DAY);
  const hours = Math.floor((diffMs % MS_PER_DAY) / MS_PER_HOUR);
  const minutes = Math.floor((diffMs % MS_PER_HOUR) / MS_PER_MINUTE);
  const seconds = Math.floor((diffMs % MS_PER_MINUTE) / MS_PER_SECOND);

  return { days, hours, minutes, seconds };
}

export function parseContactedAtMs(value: string | null | undefined): number | null {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) {
    return null;
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const startMs = Date.parse(`${trimmed}T00:00:00-03:00`);
    return Number.isFinite(startMs) ? startMs : null;
  }

  const localMatch = trimmed.match(LOCAL_DATE_TIME_PATTERN);
  if (localMatch && !/[zZ]|[+-]\d{2}:\d{2}$/.test(trimmed)) {
    const seconds = localMatch[4] ?? '00';
    const startMs = Date.parse(`${localMatch[1]}T${localMatch[2]}:${localMatch[3]}:${seconds}-03:00`);
    return Number.isFinite(startMs) ? startMs : null;
  }

  const startMs = Date.parse(trimmed);
  return Number.isFinite(startMs) ? startMs : null;
}

/** Build ISO with America/Sao_Paulo offset from date + time fields. */
export function buildBrazilContactedAt(date: string, time: string): string | null {
  if (!DATE_ONLY_PATTERN.test(date.trim()) || !/^\d{2}:\d{2}$/.test(time.trim())) {
    return null;
  }

  return `${date.trim()}T${time.trim()}:00-03:00`;
}

/** Split a contact instant into Brazil date/time for form inputs. */
export function splitBrazilContactedAt(value: string | null | undefined): { date: string; time: string } | null {
  const startMs = parseContactedAtMs(value);
  if (startMs == null) {
    return null;
  }

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(startMs));

  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  if (!byType.year || !byType.month || !byType.day || !byType.hour || !byType.minute) {
    return null;
  }

  return {
    date: `${byType.year}-${byType.month}-${byType.day}`,
    time: `${byType.hour}:${byType.minute}`,
  };
}

function pad2(value: number): string {
  return String(Math.max(0, Math.trunc(value))).padStart(2, '0');
}

/** Chronometer time part: `12d 04:03:15` or `04:03:15` when still under 1 day. */
export function formatElapsedClock(
  elapsed: MariContactElapsed | null | undefined,
): string | null {
  if (
    !elapsed
    || !Number.isFinite(elapsed.days)
    || !Number.isFinite(elapsed.hours)
    || !Number.isFinite(elapsed.minutes)
    || !Number.isFinite(elapsed.seconds)
  ) {
    return null;
  }

  const days = Math.max(0, Math.trunc(elapsed.days));
  const clock = `${pad2(elapsed.hours)}:${pad2(elapsed.minutes)}:${pad2(elapsed.seconds)}`;

  if (days === 0) {
    return clock;
  }

  return `${days}d ${clock}`;
}

/** Full label: `Último contato há 12d 04:03:15` or empty-state copy. */
export function formatElapsedSinceContact(
  elapsed: MariContactElapsed | null | undefined,
): string {
  const clock = formatElapsedClock(elapsed);
  if (!clock) {
    return 'Sem contato registrado';
  }

  return `Último contato há ${clock}`;
}
